/**
 * ukMobile.ts — is this a UK MOBILE number, and if not, what should we say?
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: journey capture.
 *
 * WHY MOBILE AND NOT "ANY PHONE" (CTO, 2026-09-11): the number a patient gives
 * us is how we reach them by SMS and WhatsApp later, and neither works on a
 * landline. A landline accepted today is a follow-up that silently cannot be
 * sent next week — and nobody finds out, because the send just never lands.
 * So patient-facing capture asks for a mobile, and says why.
 *
 * ⚠️ THIS IS A MIRROR, NOT THE CONTROL (R41). clinic-service's
 * `src/common/uk-mobile.ts` holds the same rule and is authoritative — it
 * rejects the request whatever the client does. This copy exists for one
 * reason: to tell the patient AT THE FIELD, before they submit, because
 * neither CaptureStep nor the widgets parse a server 400 into a readable
 * sentence, so a server-only rule would surface as a generic failure and lose
 * the lead. The two share one set of test vectors (see the spec) so they
 * cannot drift apart unnoticed.
 *
 * TODO(contracts): promote `isUkMobile` into @thegpservice/gpsclinics-contracts
 * and import it in both repos. Deferred only because consumers pin v0.7.1 and
 * the package is at v0.12.1 — the bump would drag in five minor versions of
 * unrelated changes. The established contracts-gap pattern, same as
 * `leadCaptureRequestWidened`.
 *
 * ── THE RULE (Ofcom National Telephone Numbering Plan) ────────────────────────
 *   071–075, 077–079  mobile
 *   07624             mobile (Isle of Man)
 *   070               personal numbering — NOT mobile, and a known scam range
 *   076 (not 07624)   radiopaging — NOT mobile
 *   01, 02            geographic landlines
 *   03, 08, 09        non-geographic / freephone / premium — NOT mobile
 *
 * Accepted input shapes, after spaces, hyphens, dots and brackets are removed:
 * `07…`, `+447…`, `00447…`, `447…` (12 digits), and the common
 * `+44 (0) 7…` form, whose bracketed trunk zero becomes a stray `0`.
 */

/** Separators a person types into a phone number, removed before any check. */
const SEPARATORS = /[\s\-.()]/g;

/** A UK mobile, in national form: 07 + a mobile range. 11 digits exactly. */
const UK_MOBILE_NATIONAL = /^07(?:[1-57-9]\d{8}|624\d{6})$/;

/** Geographic landline, national form — used only to word the hint kindly. */
const UK_LANDLINE_NATIONAL = /^0[12]\d{8,9}$/;

/**
 * The number in UK national form (`07…`), or null if it is not a UK number in
 * any shape we accept. Normalises; does not validate the range.
 */
export function toUkNational(raw: string): string | null {
  let digits = raw.trim().replace(SEPARATORS, '');
  if (digits.startsWith('+44')) digits = digits.slice(3);
  else if (digits.startsWith('0044')) digits = digits.slice(4);
  else if (digits.startsWith('44') && digits.length === 12) digits = digits.slice(2);
  else if (digits.startsWith('0')) digits = digits.slice(1);
  else return null;
  // The `+44 (0) 7…` form leaves the bracketed trunk zero behind.
  if (digits.startsWith('0')) digits = digits.slice(1);
  if (!/^\d+$/.test(digits)) return null;
  return `0${digits}`;
}

/** True only for a UK mobile number, in any of the accepted input shapes. */
export function isUkMobile(raw: string): boolean {
  const national = toUkNational(raw);
  return national !== null && UK_MOBILE_NATIONAL.test(national);
}

/**
 * The sentence to show under the field when the number is not a UK mobile.
 *
 * Says WHY, because "invalid phone number" to someone who typed their real
 * home number reads as the site being broken. A recognised landline gets a
 * gentler, specific line; anything else gets the general one.
 */
export function ukMobileHint(raw: string): string {
  const national = toUkNational(raw);
  if (national !== null && UK_LANDLINE_NATIONAL.test(national)) {
    return "That looks like a landline. Please use a mobile number (starting 07) — it's how we text you updates.";
  }
  return 'Please enter a UK mobile number starting 07 — we use it to text you updates.';
}
